/**
 * Custom hook to handle mode-specific save operations for proxy edit panel.
 * Extracts save dispatch logic to reduce cognitive complexity.
 */
import { useAppDispatch } from '../../../../store/hooks'
import type { ProxyMode } from '../../../../components/common/config/adminConfig'
import type { EditablePerson } from './ProxyEditPanelUtils'
import {
  assignApproverProxies,
  fetchCurrentApproverProxies,
  type AssignApproverProxyEntry,
} from '../../../../store/slices/admin/approverProxySlice'
import {
  assignRequestorProxies,
  fetchCurrentRequestorProxies,
} from '../../../../store/slices/admin/requestorProxySlice'
import {
  assignFloatEmployees,
  fetchCurrentFloatEmployees,
  type AssignFloatEmployeeEntry,
} from '../../../../store/slices/admin/floatEmployeeSlice'

interface SavePayload {
  approvalGrpId: string
  regionCode: string
  primaryApproverName: string
  approverGroup: EditablePerson[]
}

/**
 * Custom hook providing mode-specific save handlers for proxy assignments.
 * @param mode - Current proxy mode ('approver', 'float', or 'requestor')
 * @returns Save handler function and success callback
 */
export const useProxySave = (mode: ProxyMode) => {
  const dispatch = useAppDispatch()

  /**
   * Saves approver proxy assignments.
   */
  const saveApproverProxies = async (payload: SavePayload): Promise<boolean> => {
    const proxyApproverNuid =
      sessionStorage.getItem('userId')?.toUpperCase() ?? ''
    
    const currentApproverProxies: AssignApproverProxyEntry[] =
      payload.approverGroup.map((p) => ({
        fromDate: p.startDate ?? '',
        toDate: p.endDate ?? '',
        region: p.region,
        department: p.department,
        selectedAPProxyName: p.name,
        // Normalize display-only sentinel values before sending to the API.
        email: p.email === 'N/A' || p.email === '?' ? '' : (p.email ?? ''),
        existingApproverProxy: p.isNewlyAdded ? '1' : '0',
      }))

    const result = await dispatch(
      assignApproverProxies({
        approvalGrpId: payload.approvalGrpId,
        regionCode: payload.regionCode,
        primaryApproverName: payload.primaryApproverName,
        proxyApproverNuid,
        saveNone: payload.approverGroup.length === 0,
        currentApproverProxies,
      })
    )

    if (!assignApproverProxies.rejected.match(result)) {
      await dispatch(fetchCurrentApproverProxies(payload.approvalGrpId))
      return true
    }
    return false
  }

  /**
   * Saves float employee assignments.
   */
  const saveFloatEmployees = async (payload: SavePayload): Promise<boolean> => {
    const currentFloatEmployees: AssignFloatEmployeeEntry[] =
      payload.approverGroup.map((p) => ({
        selectedFEmployeeName: p.name,
        fromDate: p.startDate ?? '',
        toDate: p.endDate ?? '',
        department: p.department,
        region: p.region,
        // Normalize display-only sentinel values before sending to the API.
        email: p.email === 'N/A' || p.email === '?' ? '' : (p.email ?? ''),
      }))

    const result = await dispatch(
      assignFloatEmployees({
        approvalGrpId: payload.approvalGrpId,
        regionCode: payload.regionCode,
        primaryApproverName: payload.primaryApproverName,
        saveNone: payload.approverGroup.length === 0,
        currentFloatEmployees,
      })
    )

    if (!assignFloatEmployees.rejected.match(result)) {
      await dispatch(fetchCurrentFloatEmployees(payload.approvalGrpId))
      return true
    }
    return false
  }

  /**
   * Saves requestor proxy assignments.
   */
  const saveRequestorProxies = async (payload: SavePayload): Promise<boolean> => {
    const result = await dispatch(
      assignRequestorProxies({
        approvalGrpId: payload.approvalGrpId,
        currentProxies: payload.approverGroup.map((p) => p.id),
      })
    )

    if (!assignRequestorProxies.rejected.match(result)) {
      await dispatch(fetchCurrentRequestorProxies(payload.approvalGrpId))
      return true
    }
    return false
  }

  /**
   * Dispatches the appropriate save thunk based on mode.
   * @returns true if save succeeded, false if rejected
   */
  const handleSave = async (payload: SavePayload): Promise<boolean> => {
    if (mode === 'approver') {
      return saveApproverProxies(payload)
    }
    if (mode === 'float') {
      return saveFloatEmployees(payload)
    }
    return saveRequestorProxies(payload)
  }

  return { handleSave }
}
